/**
 * ============================================================================
 * useAppShortcutsPrefs — Preferências de atalhos do App Launcher
 * ----------------------------------------------------------------------------
 * RELEASE: 2026-10-02 v1.2.0 - Estrela não "desmarca" mais após salvar:
 *   (1) onSuccess grava no cache o valor confirmado em vez de reler do
 *   servidor (sem invalidate → sem corrida de refetch); invalida só em erro.
 *   (2) toggleFavorite parte do estado mais recente do cache (cliques rápidos
 *   seguidos não se sobrescrevem). (3) queryFn mantém o cache anterior se a
 *   leitura vier sem app_shortcuts/malformada, em vez de zerar os favoritos.
 * RELEASE: 2026-10-02 v1.1.0 - Chave de cache dedicada (`user-preferences-shortcuts`).
 * RELEASE: 2026-07-24 v1.0.0 - Criação. Lê e persiste { favorites, order } via
 *   RPCs get_user_preferences / set_user_preferences. Expor helpers
 *   toggleFavorite / setFavorites / reorderFavorites com atualização otimista.
 * ============================================================================
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const log = createLogger('useAppShortcutsPrefs');

/** Estrutura das preferências de atalhos do usuário. */
export interface AppShortcutsPrefs {
  favorites: string[];
  order: string[];
}

type CacheShape = { app_shortcuts: AppShortcutsPrefs };

const EMPTY: AppShortcutsPrefs = { favorites: [], order: [] };
// Chave dedicada: NÃO compartilhar com outros hooks de preferências (formatos diferentes).
const QUERY_KEY = (userId?: string) => ['user-preferences-shortcuts', userId] as const;

/** Mantém apenas strings válidas de um array vindo do servidor. */
function toStringArray(v: unknown): string[] | null {
  if (!Array.isArray(v)) return null;
  return v.filter((x): x is string => typeof x === 'string' && x.length > 0);
}

/** Lê as preferências do usuário e devolve apenas o slice app_shortcuts. */
export function useAppShortcutsPrefs() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const key = QUERY_KEY(user?.id);

  const query = useQuery({
    queryKey: key,
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CacheShape> => {
      const { data, error } = await (supabase.rpc as any)('get_user_preferences');
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      const raw = row?.app_shortcuts;
      const favorites = toStringArray(raw?.favorites);
      // v1.2.0: leitura incompleta não zera o que a tela já tem.
      if (favorites === null) {
        const previous = queryClient.getQueryData<CacheShape>(key);
        log.warn('Leitura sem app_shortcuts válido; mantendo cache anterior');
        return previous ?? { app_shortcuts: EMPTY };
      }
      return {
        app_shortcuts: {
          favorites,
          order: toStringArray(raw?.order) ?? [],
        },
      };
    },
  });

  const prefs: AppShortcutsPrefs = query.data?.app_shortcuts ?? EMPTY;

  /** Persiste um novo estado completo de atalhos (atualização otimista). */
  const mutation = useMutation({
    mutationFn: async (next: AppShortcutsPrefs) => {
      const { error } = await (supabase.rpc as any)('set_user_preferences', {
        p_prefs: { app_shortcuts: next },
      });
      if (error) throw error;
      return next;
    },
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CacheShape>(key);
      queryClient.setQueryData<CacheShape>(key, { app_shortcuts: next });
      return { previous };
    },
    onError: (err: Error, _next, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(key, ctx.previous);
      }
      log.error('Falha ao salvar atalhos', { error: err });
      toast.error(`Erro ao salvar atalhos: ${err.message}`);
      // Só relê do servidor em caso de erro, para ressincronizar.
      queryClient.invalidateQueries({ queryKey: key });
    },
    onSuccess: (saved) => {
      // v1.2.0: grava o valor confirmado — sem refetch (evita corrida).
      queryClient.setQueryData<CacheShape>(key, { app_shortcuts: saved });
    },
  });

  /** Estado mais recente (inclui atualização otimista de cliques anteriores). */
  const current = useCallback(
    (): AppShortcutsPrefs => queryClient.getQueryData<CacheShape>(key)?.app_shortcuts ?? EMPTY,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, user?.id],
  );

  const toggleFavorite = useCallback(
    (shortcutKey: string) => {
      const now = current();
      const isFav = now.favorites.includes(shortcutKey);
      const nextFavorites = isFav
        ? now.favorites.filter((k) => k !== shortcutKey)
        : [...now.favorites, shortcutKey];
      mutation.mutate({ favorites: nextFavorites, order: now.order });
    },
    [current, mutation],
  );

  const setFavorites = useCallback(
    (favorites: string[]) => {
      mutation.mutate({ favorites, order: current().order });
    },
    [current, mutation],
  );

  return {
    loading: query.isLoading,
    favorites: prefs.favorites,
    order: prefs.order,
    isFavorite: (k: string) => prefs.favorites.includes(k),
    toggleFavorite,
    setFavorites,
    saving: mutation.isPending,
  };
}
